import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import {
  BookOpen, Loader2, Check, Sparkles, Users, MessageCircle,
  Calendar, Compass, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const Checkout = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      // Break out of iframe (Stripe Checkout refuses to load inside iframes)
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = data.checkout_url;
          return;
        }
      } catch (_) {
        // Cross-origin iframe — fall back to opening in a new tab
        window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
        setLoading(false);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const features = [
    { icon: Sparkles, text: 'AI-powered match suggestions' },
    { icon: Compass, text: 'Unlimited match discovery' },
    { icon: Users, text: 'Connect with unlimited book lovers' },
    { icon: MessageCircle, text: 'In-app messaging with matches' },
    { icon: Calendar, text: 'Join book clubs & schedule meetings' },
    { icon: BookOpen, text: 'Discussion threads & book library' }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-[#E1D9CD]">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#6B7A4F]" />
            <span className="text-xl font-bold text-[#2D2A26]">Bookish Bestie</span>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            data-testid="checkout-logout-btn"
            className="border-[#6B7A4F] text-[#6B7A4F]"
          >
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <div className="text-center mb-8">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B7A4F]">
            One Small Step to Meeting Your Bookish Bestie
          </span>
          <h1 className="text-4xl sm:text-5xl font-light text-[#2D2A26] mt-3 mb-4">
            Hi {user?.name}, unlock <span className="font-bold">Bookish Bestie</span>
          </h1>
          <p className="text-lg text-[#5C5955]">
            A one-time $10 gets you lifetime access. No subscriptions. No renewals.
          </p>
        </div>

        <div className="glass-card p-8 rounded-2xl mb-6" data-testid="checkout-container">
          <div className="text-center mb-6">
            <div className="inline-flex items-baseline gap-1">
              <span className="text-6xl font-light text-[#6B7A4F]">$10</span>
              <span className="text-lg text-[#5C5955]">one-time</span>
            </div>
            <p className="text-sm text-[#5C5955] mt-2">Lifetime access · No hidden fees</p>
          </div>

          <div className="border-t border-[#E1D9CD] pt-6 mb-6">
            <h3 className="font-semibold text-[#2D2A26] mb-4">Everything included:</h3>
            <ul className="space-y-3">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#E2E6D5] flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#6B7A4F]" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <span className="text-[#2D2A26]">{f.text}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <Button
            onClick={handleCheckout}
            disabled={loading}
            data-testid="checkout-submit-btn"
            className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white py-6 rounded-full text-lg font-semibold"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Redirecting to secure checkout...</>
            ) : (
              'Get Instant Access · $10'
            )}
          </Button>

          <p className="text-xs text-[#5C5955] text-center mt-4">
            Secure payment via Stripe · <a href="/terms" className="underline hover:text-[#6B7A4F]">Terms</a> · <a href="/privacy" className="underline hover:text-[#6B7A4F]">Privacy</a>
          </p>
        </div>

        <div className="text-center">
          <p className="text-xs text-[#5C5955]">
            <strong className="text-[#6B7A4F]">No refunds:</strong> Once purchased, you can see your matches immediately.
            All sales are final. See our <a href="/terms" className="underline">Terms</a> for details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
